import { createRoute, lazyRouteComponent, stripSearchParams } from '@tanstack/react-router'

import { guardRoute } from '../../auth/guard.ts'
import { parseOnboardingStep, type OnboardingSearch } from '../../data/club-onboarding.ts'
import {
  NO_PLAYER_FILTERS,
  PLAYER_ACCOUNT_FILTERS,
  PLAYER_SORTS,
  PLAYER_STATUSES,
  WITHOUT_TEAM,
  type PlayerFilters,
  type PlayerSort,
  type PlayerTeamFilter,
} from '../../data/player-filters.ts'
import { parsePlayerPosition } from '../../messages/index.ts'
import { NAV_PATHS } from '../../navigation/paths.ts'
import { parseOneOf, parsePositiveInt } from '../params.ts'
import { rootRoute } from '../root.tsx'

// The secretario's screen: one società's tesserati. The API pages the season and the screen
// narrows it, so every filter lives in the URL and none reaches the server.

export interface PlayersSearch extends PlayerFilters, OnboardingSearch {
  readonly sort: PlayerSort
  readonly page: number
}

function parseTeam(value: unknown): PlayerTeamFilter | null {
  return value === WITHOUT_TEAM ? WITHOUT_TEAM : parsePositiveInt(value)
}

/** The one search state a fresh visit to the list starts from: every param at its default.
 *  Also what `stripSearchParams` keeps out of the URL — a `q` that has no value is not a
 *  search, and `?q=null` is not «no search», it is a literal string that filters nothing. */
const DEFAULT_PLAYERS_SEARCH: PlayersSearch = {
  ...NO_PLAYER_FILTERS,
  sort: 'last_name',
  page: 1,
}

export const officialPlayersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: NAV_PATHS.officialPlayers,
  // An invented value in the URL becomes the harmless default rather than reaching a label.
  validateSearch: (search: Record<string, unknown>): PlayersSearch => ({
    q: typeof search.q === 'string' ? search.q : null,
    team: parseTeam(search.team),
    position: parsePlayerPosition(search.position),
    year: parsePositiveInt(search.year),
    account: parseOneOf(PLAYER_ACCOUNT_FILTERS, search.account) ?? DEFAULT_PLAYERS_SEARCH.account,
    status: parseOneOf(PLAYER_STATUSES, search.status) ?? DEFAULT_PLAYERS_SEARCH.status,
    sort: parseOneOf(PLAYER_SORTS, search.sort) ?? DEFAULT_PLAYERS_SEARCH.sort,
    page: parsePositiveInt(search.page) ?? DEFAULT_PLAYERS_SEARCH.page,
    onboarding: parseOnboardingStep(search.onboarding),
  }),
  search: {
    middlewares: [stripSearchParams(DEFAULT_PLAYERS_SEARCH)],
  },
  beforeLoad: ({ context, location }) =>
    guardRoute({
      queryClient: context.queryClient,
      requiredRoles: ['club'],
      currentPath: location.href,
    }),
  staticData: { opensOnHero: true },
  component: lazyRouteComponent(() => import('../../pages/official/players.tsx')),
})

/** The link other pages take the path from, instead of respelling it. */
export const officialPlayersLink = {
  to: NAV_PATHS.officialPlayers,
  search: DEFAULT_PLAYERS_SEARCH,
} as const
